import React, { useState, useEffect } from "react";
import { FiUserPlus, FiX, FiUsers, FiUserCheck } from "react-icons/fi";
import api from "../../api";
import "./AssignmentManager.css";

const AssignmentManager = ({ 
  personId, 
  assignments = { primary: [], secondary: [] },
  users = [],
  onUpdate,
  showToast = (msg, type) => console.log(msg, type)
}) => {
  const [primaryAssignments, setPrimaryAssignments] = useState(assignments.primary || []);
  const [secondaryAssignments, setSecondaryAssignments] = useState(assignments.secondary || []);
  const [showPrimaryDropdown, setShowPrimaryDropdown] = useState(false);
  const [showSecondaryDropdown, setShowSecondaryDropdown] = useState(false);

  useEffect(() => {
    setPrimaryAssignments(assignments.primary || []);
    setSecondaryAssignments(assignments.secondary || []);
  }, [assignments]);

  const addAssignment = async (userId, type) => {
    try {
      const response = await api.post("/people-assignments", {
        person_id: personId,
        user_id: userId,
        assignment_type: type
      });

      const newAssignment = response.data;
      
      if (type === "primary") {
        setPrimaryAssignments([...primaryAssignments, newAssignment]);
      } else {
        setSecondaryAssignments([...secondaryAssignments, newAssignment]);
      }

      if (onUpdate) onUpdate();
      showToast(`User assigned as ${type}`, "success");
      
      setShowPrimaryDropdown(false);
      setShowSecondaryDropdown(false);
    } catch (error) {
      console.error("Error adding assignment:", error);
      showToast(error.message, "error");
    }
  };

  const removeAssignment = async (assignmentId, type) => {
    try {
      await api.delete(`/people-assignments/${assignmentId}`);

      if (type === "primary") {
        setPrimaryAssignments(primaryAssignments.filter(a => a.assignment_id !== assignmentId));
      } else {
        setSecondaryAssignments(secondaryAssignments.filter(a => a.assignment_id !== assignmentId));
      }

      if (onUpdate) onUpdate();
      showToast("Assignment removed", "success");
    } catch (error) {
      console.error("Error removing assignment:", error);
      showToast("Failed to remove assignment", "error");
    }
  };

  const getAvailableUsers = (currentAssignments) => {
    const assignedUserIds = [
      ...primaryAssignments.map(a => a.user_id),
      ...secondaryAssignments.map(a => a.user_id)
    ];
    return users.filter(user => !assignedUserIds.includes(user.id));
  };

  const renderAssignmentChip = (assignment, type) => {
    const isInactive = assignment.active === "n";
    const chipColor = isInactive ? "#F08080" : (assignment.chip_color || "#e0e0e0");
    
    return (
      <div
        key={assignment.assignment_id}
        className="assignment-chip"
        style={{ backgroundColor: chipColor }}
      >
        <span className="assignment-chip-text">
          {assignment.first_name} {assignment.last_name}
          {isInactive && " (Inactive)"}
        </span>
        <button
          className="assignment-chip-remove"
          onClick={() => removeAssignment(assignment.assignment_id, type)}
          title="Remove assignment"
        >
          <FiX size={14} />
        </button>
      </div>
    );
  };

  const renderDropdown = (type) => {
    const availableUsers = getAvailableUsers();
    const showDropdown = type === "primary" ? showPrimaryDropdown : showSecondaryDropdown;
    const setShowDropdown = type === "primary" ? setShowPrimaryDropdown : setShowSecondaryDropdown;

    if (!showDropdown) return null;

    return (
      <div className="assignment-dropdown">
        <div className="assignment-dropdown-overlay" onClick={() => setShowDropdown(false)} />
        <div className="assignment-dropdown-content">
          {availableUsers.length === 0 ? (
            <div className="assignment-dropdown-empty">All users assigned</div>
          ) : (
            availableUsers.map(user => (
              <div
                key={user.id}
                className="assignment-dropdown-item"
                onClick={() => addAssignment(user.id, type)}
              >
                <div
                  className="assignment-dropdown-chip"
                  style={{ backgroundColor: user.chip_color || "#e0e0e0" }}
                />
                <span>
                  {user.first_name} {user.last_name}
                  {user.active === "n" && " (Inactive)"}
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="assignment-manager">
      <div className="assignment-section">
        <div className="assignment-section-header">
          <FiUserCheck size={16} />
          <span className="assignment-section-title">Primary Users</span>
        </div>
        <div className="assignment-chips-container">
          {primaryAssignments.map(assignment => renderAssignmentChip(assignment, "primary"))}
          <div className="assignment-add-container">
            <button
              className="assignment-add-btn"
              onClick={() => setShowPrimaryDropdown(!showPrimaryDropdown)}
              title="Add primary user"
            >
              <FiUserPlus size={16} />
            </button>
            {renderDropdown("primary")}
          </div>
        </div>
      </div>

      <div className="assignment-section">
        <div className="assignment-section-header">
          <FiUsers size={16} />
          <span className="assignment-section-title">Secondary Users</span>
        </div>
        <div className="assignment-chips-container">
          {secondaryAssignments.map(assignment => renderAssignmentChip(assignment, "secondary"))}
          <div className="assignment-add-container">
            <button
              className="assignment-add-btn"
              onClick={() => setShowSecondaryDropdown(!showSecondaryDropdown)}
              title="Add secondary user"
            >
              <FiUserPlus size={16} />
            </button>
            {renderDropdown("secondary")}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignmentManager;

